/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
  useRef,
} from "react";
import { TableBuilder, TableBuilderColumn } from "baseui/table-semantic";
import { FileInfo, Folder } from "Api";
import { sortFileInfo } from "../Api/FileInfo";
import {
  fileUploadDateAsText,
  formattedDateTime,
  mbsSizeAsText,
} from "../Api/ApiSerialization";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { Checkbox, CheckboxOverrides } from "baseui/checkbox";
import { UUID } from "../Utils/Types";
import { Endpoints } from "../Utils/Common";
import { AxiosContext } from "../Utils/AuthContext";
import { FiFile, FiFolder } from "react-icons/fi";
import { useWindowSize } from "../ProjectState/ProjectUtils";
import { TableNameButton } from "./TableNameButton";

type ThemeColors = ReturnType<typeof useDexterTheme>["currentTheme"]["colors"];

/**
 * The table shows folders and files in one list. A row is one or the other;
 * folders are always listed before files.
 */
export type TableRow =
  | { kind: "folder"; folder: Folder }
  | { kind: "file"; file: FileInfo };

const rowId = (row: TableRow): UUID =>
  row.kind === "folder" ? row.folder.id : row.file.id;

// Maps a detected file type to one of the theme's badge color slots
const BADGE_TYPES: Record<
  string,
  { label: string; slot: "proto" | "xml" | "unknown" }
> = {
  PROTO: { label: "Protobuf", slot: "proto" },
  XML: { label: "XML", slot: "xml" },
  UNDETECTED: { label: "Unknown", slot: "unknown" },
};

function FormatBadge({
  fileType,
  colors,
}: {
  fileType: string;
  colors: ThemeColors;
}): JSX.Element {
  const match = BADGE_TYPES[fileType.toUpperCase()];
  const label = match?.label ?? fileType;
  const color = colors.fileTypeBadgeColors[match?.slot ?? "unknown"];
  // Badge colors are tuned as foreground colors against each theme's surface,
  // so they're used for text + outline rather than as a solid fill.
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 8px",
        borderRadius: "999px",
        border: `1px solid ${color}`,
        color,
        backgroundColor: "transparent",
        fontSize: "11px",
        fontWeight: 600,
        lineHeight: "16px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

const makeCheckboxOverrides = (colors: ThemeColors): CheckboxOverrides => ({
  Root: { style: { alignItems: "center", backgroundColor: "transparent" } },
  Checkmark: {
    style: ({ $checked, $isIndeterminate, $disabled }: any) => {
      const on = $checked || $isIndeterminate;
      return {
        width: "16px",
        height: "16px",
        borderRadius: "4px",
        borderTopWidth: "1px",
        borderBottomWidth: "1px",
        borderLeftWidth: "1px",
        borderRightWidth: "1px",
        borderTopColor: on
          ? colors.primaryButtonColor
          : colors.dropdownBorderColor,
        borderBottomColor: on
          ? colors.primaryButtonColor
          : colors.dropdownBorderColor,
        borderLeftColor: on
          ? colors.primaryButtonColor
          : colors.dropdownBorderColor,
        borderRightColor: on
          ? colors.primaryButtonColor
          : colors.dropdownBorderColor,
        backgroundColor: on
          ? colors.primaryButtonColor
          : colors.tableBackgroundColor,
        opacity: $disabled ? 0.4 : 1,
      };
    },
  },
});

export const DEFAULT_PAGE_SIZE = 8;

const HIDDEN_FILE_PREFIX = "demo-";

// Files the table lists. Use this in the parent too so the page count matches.
export const isListableFile = (file: FileInfo): boolean => {
  const fileName = (file.attributes?.name || "").toLowerCase();
  if (fileName.startsWith(HIDDEN_FILE_PREFIX)) return false;
  return fileName.split(".").pop() !== "pdf";
};
// Body cell is height: 46px plus a 1px bottom border
export const TABLE_ROW_HEIGHT = 47;
// Head cell: 12px + 16px line-height + 12px padding + 1px border
export const TABLE_HEADER_HEIGHT = 41;
const MIN_PAGE_SIZE = 3;

/**
 * @param reservedHeight Height of everything on the page that isn't table rows:
 * app header, search bar, toolbar, pagination, margins.
 */
export const useResponsivePageSize = (reservedHeight: number): number => {
  const { height } = useWindowSize();

  return useMemo(() => {
    const available = height - reservedHeight - TABLE_HEADER_HEIGHT;
    return Math.max(MIN_PAGE_SIZE, Math.floor(available / TABLE_ROW_HEIGHT));
  }, [height, reservedHeight]);
};

export interface FileSearchResultTableProps {
  files: FileInfo[];
  /** Folders to list above the files (folders are always shown first). */
  folders?: Folder[];
  /** Opens a folder (the parent navigates into it). */
  onOpenFolder?: (folder: Folder) => void;
  /** Opens a file (the parent shows its contents in a modal). */
  onOpenFile?: (file: FileInfo) => void;
  onCheckedMapChange?: (map: Record<UUID, boolean>) => void;
  checkedMap?: Record<UUID, boolean>;
  currentPage?: number;
  pageSize?: number;
  onSortChange?: () => void;
  emptyMessage?: React.ReactNode;
}

export default function FileSearchResultTable({
  files,
  folders = [],
  onOpenFolder,
  onOpenFile,
  onCheckedMapChange,
  checkedMap = {},
  currentPage = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  onSortChange,
  emptyMessage = "No files match the search text",
}: FileSearchResultTableProps): JSX.Element {
  const { currentTheme } = useDexterTheme();
  const { colors } = currentTheme;
  const checkboxOverrides = useMemo(
    () => makeCheckboxOverrides(colors),
    [colors]
  );
  const axiosContext = useContext(AxiosContext);

  const [filesWithSavedMappings, setFilesWithSavedMappings] = useState<
    Set<UUID>
  >(new Set());

  const sortableFiles = useMemo(() => files.filter(isListableFile), [files]);

  const [sortColumn, setSortColumn] = useState("uploadDateUtc");
  const [sortAsc, setSortAsc] = useState(true);

  // Sort the full list first so sorting works across pages, then slice. Folders
  // are always listed before files; the column sort only orders the files.
  const sortedRows = useMemo<TableRow[]>(() => {
    const folderRows: TableRow[] = [...folders]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((folder) => ({ kind: "folder", folder }));

    const fileRows: TableRow[] = sortFileInfo(
      sortableFiles,
      sortAsc,
      sortColumn
    ).map((file) => ({ kind: "file", file }));

    return [...folderRows, ...fileRows];
  }, [folders, sortableFiles, sortAsc, sortColumn]);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, currentPage, pageSize]);

  // Files on the current page, for the saved-mapping check below.
  const pageFiles = useMemo(
    () => pageRows.flatMap((row) => (row.kind === "file" ? [row.file] : [])),
    [pageRows]
  );

  const handleSort = useCallback(
    (columnId: string) => {
      if (columnId === sortColumn) {
        setSortAsc((prev) => !prev);
      } else {
        setSortColumn(columnId);
        setSortAsc(true);
      }
      onSortChange?.();
    },
    [sortColumn, onSortChange]
  );

  // Check saved mappings / demo membership only for files on the visible page.
  // Results are cached, so returning to a page doesn't re-query it.
  const checkedIdsRef = useRef<Set<UUID>>(new Set());

  useEffect(() => {
    let isMounted = true;
    const toCheck = pageFiles.filter(
      (file) => !checkedIdsRef.current.has(file.id)
    );
    if (toCheck.length === 0) return undefined;

    async function checkFile(
      file: FileInfo
    ): Promise<{ id: UUID; hasMapping: boolean }> {
      try {
        const response = await axiosContext.get(
          `${Endpoints.FindSavedMappingsWithFileId}?fileId=${file.id}`
        );
        return { id: file.id, hasMapping: !!response.data?.length };
      } catch (error) {
        console.error(`Error checking file ${file.id}:`, error);
        return { id: file.id, hasMapping: false };
      }
    }

    Promise.all(toCheck.map(checkFile)).then((results) => {
      if (!isMounted) return;
      results.forEach((r) => checkedIdsRef.current.add(r.id));
      const mappingIds = results
        .filter((result) => result.hasMapping)
        .map((result) => result.id);
      if (mappingIds.length) {
        setFilesWithSavedMappings((prev) => new Set([...prev, ...mappingIds]));
      }
    });

    return () => {
      isMounted = false;
    };
  }, [pageFiles, axiosContext]);

  const NOT_DELETABLE_HINT = "Used by a saved mapping, so it can't be removed";

  // A file used by a saved mapping can't be selected/removed. Folders have no
  // such constraint, so they are always selectable.
  const isDeletable = useCallback(
    (fileId: UUID) => !filesWithSavedMappings.has(fileId),
    [filesWithSavedMappings]
  );

  // A folder is always individually selectable; a file is selectable unless a
  // saved mapping uses it.
  const isRowSelectable = useCallback(
    (row: TableRow) => row.kind === "folder" || isDeletable(row.file.id),
    [isDeletable]
  );

  // Selectable rows on the current page: folders (always) + files not used by a
  // saved mapping. Select-all toggles exactly these.
  const selectableIds = useMemo(
    () => pageRows.filter(isRowSelectable).map(rowId),
    [pageRows, isRowSelectable]
  );

  const checkedCount = selectableIds.filter((id) => checkedMap[id]).length;
  const parentChecked =
    checkedCount === selectableIds.length && selectableIds.length > 0;
  const parentIndeterminate = checkedCount > 0 && !parentChecked;

  const handleParentChecked = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    // When partially selected, clicking selects everything
    const checked = parentIndeterminate ? true : e.target.checked;
    const map: Record<UUID, boolean> = { ...checkedMap };
    selectableIds.forEach((id) => (map[id] = checked));
    onCheckedMapChange?.(map);
  };

  const handleRowCheck =
    (id: UUID) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      const newMap = { ...checkedMap, [id]: isChecked };
      onCheckedMapChange?.(newMap);
    };

  const lastRowIndex = pageRows.length - 1;

  const headCellStyle = useMemo(
    () => ({
      backgroundColor: colors.tableBackgroundColor,
      color: colors.secondaryTextColor,
      fontFamily: currentTheme.fonts.primary,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "16px",
      paddingTop: "12px",
      paddingBottom: "12px",
      paddingLeft: "12px",
      paddingRight: "12px",
      borderBottom: `1px solid ${colors.dropdownBorderColor}`,
      borderRight: "none",
      whiteSpace: "nowrap",
      "::before": { display: "none" },
      "::after": { display: "none" },
    }),
    [colors, currentTheme.fonts.primary]
  );

  const bodyCellStyle = useCallback(
    ({ $rowIndex, $row }: any) => ({
      height: "46px",
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: "12px",
      paddingRight: "12px",
      verticalAlign: "middle",
      backgroundColor: "transparent",
      color: colors.tableTextColor,
      fontFamily: currentTheme.fonts.primary,
      fontSize: "14px",
      whiteSpace: "nowrap",
      borderTop: "none",
      borderRight: "none",
      borderBottom:
        $rowIndex === lastRowIndex
          ? "none"
          : `1px solid ${colors.dropdownBorderColor}`,
      // Grey out files that are used by a saved mapping
      opacity:
        $row && $row.kind === "file" && !isDeletable($row.file.id) ? 0.5 : 1,
    }),
    [colors, currentTheme.fonts.primary, lastRowIndex, isDeletable]
  );

  const styleOverrides = useMemo(() => {
    const sortIcon = {
      color: colors.secondaryTextColor,
      height: "14px",
      width: "14px",
    };

    const borderColor = colors.dropdownBorderColor;
    // Match TableActionBar
    const radius = "8px";

    return {
      Root: {
        style: {
          borderTopWidth: "1px",
          borderBottomWidth: "1px",
          borderLeftWidth: "1px",
          borderRightWidth: "1px",
          borderTopStyle: "solid",
          borderBottomStyle: "solid",
          borderLeftStyle: "solid",
          borderRightStyle: "solid",
          borderTopColor: borderColor,
          borderBottomColor: borderColor,
          borderLeftColor: borderColor,
          borderRightColor: borderColor,
          borderTopLeftRadius: radius,
          borderTopRightRadius: radius,
          borderBottomLeftRadius: radius,
          borderBottomRightRadius: radius,
          overflow: "hidden",
          boxSizing: "border-box",
          backgroundColor: colors.tableBackgroundColor,
          marginLeft: "25px",
          marginRight: "25px",
        },
      },
      Table: {
        style: {
          width: "100%",
          backgroundColor: colors.tableBackgroundColor,
        },
      },
      TableHeadRow: {
        style: { backgroundColor: colors.tableBackgroundColor },
      },
      TableHeadCell: { style: headCellStyle },
      TableHeadCellSortable: {
        style: {
          ...headCellStyle,
          ":hover": {
            backgroundColor: colors.tableBackgroundColor,
            color: colors.tableTextColor,
          },
          ":focus": { outline: "none" },
        },
      },
      SortNoneIcon: { style: { display: "none" } },
      SortAscIcon: { style: sortIcon },
      SortDescIcon: { style: sortIcon },
      TableBodyRow: {
        style: {
          backgroundColor: colors.tableBackgroundColor,
          ":hover": { backgroundColor: colors.dropdownItemHoverBackground },
        },
      },
      TableBodyCell: { style: bodyCellStyle },
      TableEmptyMessage: {
        style: { backgroundColor: colors.tableBackgroundColor },
      },
    };
  }, [colors, headCellStyle, bodyCellStyle]);

  type ColumnOverrides = React.ComponentProps<
    typeof TableBuilderColumn
  >["overrides"];

  // Column-level overrides replace the table-level cell styles, so these
  // re-apply the full base styles and then add the fixed width.
  const fixedWidth = (width: string, extra: object = {}): ColumnOverrides => ({
    TableHeadCell: { style: { ...headCellStyle, width, ...extra } },
    TableBodyCell: {
      style: (props: any) => ({ ...bodyCellStyle(props), width, ...extra }),
    },
  });

  return (
    <div style={{ width: "100%", MozUserSelect: "none" }}>
      <TableBuilder
        data={pageRows}
        sortColumn={sortColumn}
        sortOrder={sortAsc ? "ASC" : "DESC"}
        onSort={handleSort}
        overrides={styleOverrides}
        emptyMessage={
          <div
            style={{
              padding: "48px 0",
              textAlign: "center",
              color: colors.secondaryTextColor,
              fontSize: "14px",
            }}
          >
            {emptyMessage}
          </div>
        }
      >
        <TableBuilderColumn
          id="select"
          header={
            <Checkbox
              checked={parentChecked}
              isIndeterminate={parentIndeterminate}
              onChange={handleParentChecked}
              disabled={selectableIds.length === 0}
              overrides={checkboxOverrides}
            />
          }
          overrides={fixedWidth("24px", {
            paddingLeft: "16px",
            paddingRight: "0px",
          })}
        >
          {(row: TableRow) => {
            const selectable = isRowSelectable(row);
            const notDeletable = row.kind === "file" && !selectable;
            return (
              <span title={notDeletable ? NOT_DELETABLE_HINT : undefined}>
                <Checkbox
                  checked={!!checkedMap[rowId(row)]}
                  onChange={handleRowCheck(rowId(row))}
                  disabled={!selectable}
                  overrides={checkboxOverrides}
                />
              </span>
            );
          }}
        </TableBuilderColumn>

        <TableBuilderColumn
          id="icon"
          header={<FiFile size={16} />}
          overrides={fixedWidth("16px", { paddingRight: "0px" })}
        >
          {(row: TableRow) =>
            row.kind === "folder" ? (
              <FiFolder size={16} color={colors.collectionAccentColor} />
            ) : (
              <FiFile size={16} />
            )
          }
        </TableBuilderColumn>

        <TableBuilderColumn id="name" header="File name" sortable>
          {(row: TableRow) =>
            row.kind === "folder" ? (
              <TableNameButton
                variant="folder"
                label={row.folder.name}
                onClick={() => onOpenFolder?.(row.folder)}
              />
            ) : (
              <TableNameButton
                variant="file"
                label={row.file.attributes.name}
                onClick={() => onOpenFile?.(row.file)}
              />
            )
          }
        </TableBuilderColumn>

        <TableBuilderColumn id="fileType" header="Format" sortable>
          {(row: TableRow) =>
            row.kind === "folder" ? (
              <span style={{ color: colors.secondaryTextColor }}>—</span>
            ) : (
              <span data-testid={`${row.file.id}-fileType`}>
                <FormatBadge
                  colors={colors}
                  fileType={
                    row.file.userSelectedFileType ||
                    row.file.predictedFileType ||
                    "UNDETECTED"
                  }
                />
              </span>
            )
          }
        </TableBuilderColumn>

        <TableBuilderColumn id="sizeMbs" header="Size" sortable>
          {(row: TableRow) =>
            row.kind === "folder" ? (
              <span style={{ color: colors.secondaryTextColor }}>—</span>
            ) : (
              <span data-testid={`${row.file.id}-fileSize`}>
                {mbsSizeAsText(row.file.attributes.sizeMbs)}
              </span>
            )
          }
        </TableBuilderColumn>

        <TableBuilderColumn id="uploadDateUtc" header="Upload date" sortable>
          {(row: TableRow) =>
            row.kind === "folder" ? (
              <span data-testid={`${row.folder.id}-created`}>
                {row.folder.creationDate
                  ? formattedDateTime(row.folder.creationDate)
                  : "—"}
              </span>
            ) : (
              <span data-testid={`${row.file.id}-uploadDate`}>
                {fileUploadDateAsText(row.file)}
              </span>
            )
          }
        </TableBuilderColumn>
      </TableBuilder>
    </div>
  );
}
